import React from 'react';

import { useStyles } from './GlobalSearch.styles';
import theme from '../../../styles/theme';
import {
  Box,
  ClickAwayListener,
  Paper,
  Popper,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import AppEntityAvatar, {
  AvatarSize,
} from '../../designe/app-entity-avatar/AppEntityAvatar';
import { ICommunity } from '../../../types/interfaces/community';
import { IUser } from '../../../types/interfaces/user';
import { IExercise } from '../../../types/interfaces/exercise';
import { useNavigate } from 'react-router-dom';
import { Paths } from '../../../shared/paths';
import { useGlobalSearch } from './useGlobalSearch';
import Search from '../../reusable/search/Search';

const GlobalSearch = () => {
  const { classes } = useStyles(theme);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    data,
    filterValue,
    searchRef,
    isShowSearchResult,
    onClickAway,
    onSearchClick,
    onFilterValue,
  } = useGlobalSearch();

  function getItems(
    items: IUser[] | ICommunity[] | IExercise[],
    title: string,
    itemNameCallback: (item: IUser | ICommunity | IExercise) => string,
    linkCallback: (item: IUser | ICommunity | IExercise) => string,
  ) {
    return (
      <>
        <Typography
          variant="h3"
          className={classes.globalSearchResultCategoryName}>
          {title}
        </Typography>
        {items.map((item) => (
          <Box
            key={item.id}
            className={classes.globalSearchResultItem}
            onClick={() => navigate(linkCallback(item))}>
            <AppEntityAvatar entity={item} size={AvatarSize.s} />
            <Box>
              <Typography variant="text-md-sb">
                {itemNameCallback(item)}
              </Typography>
            </Box>
          </Box>
        ))}
      </>
    );
  }

  return (
    <ClickAwayListener onClickAway={onClickAway}>
      <Box>
        <Search
          fullWidth
          value={filterValue}
          onChange={onFilterValue}
          className={classes.globalSearch}
          onClick={() => onSearchClick()}
          ref={searchRef}
        />
        {searchRef?.current && (
          <Popper
            open={isShowSearchResult()}
            anchorEl={searchRef?.current}
            placement="bottom-start"
            className={classes.globalSearchResult}
            style={{
              width: searchRef?.current?.['clientWidth'] || 0,
            }}>
            <Paper elevation={3} className={classes.globalSearchResultPaper}>
              {data?.users?.length > 0 &&
                getItems(
                  data.users,
                  t('globalSearch.users'),
                  (user: IUser | IExercise | ICommunity) => {
                    if ('firstname' in user)
                      return `${user.firstname} ${user.lastname}`;
                  },
                  (user: IUser | IExercise | ICommunity) =>
                    `${Paths.users.root}/${user.id}`,
                )}
              {data?.communities?.length > 0 &&
                getItems(
                  data.communities,
                  t('globalSearch.communities'),
                  (community: IUser | IExercise | ICommunity) => {
                    if ('title' in community) return community.title;
                  },
                  (community: IUser | IExercise | ICommunity) =>
                    `${Paths.communities.root}/${community.id}`,
                )}
              {data?.exercises?.length > 0 &&
                getItems(
                  data.exercises,
                  t('globalSearch.exercises'),
                  (exercise: IUser | IExercise | ICommunity) => {
                    if ('title' in exercise) return exercise.title;
                  },
                  (exercise: IUser | IExercise | ICommunity) =>
                    `${Paths.exercises.root}/${exercise.id}${Paths.exercises.edit}`,
                )}
            </Paper>
          </Popper>
        )}
      </Box>
    </ClickAwayListener>
  );
};

export default GlobalSearch;
